import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import WalletButton from "./WalletButton";
import { useTheme, ACCENTS } from "../../hooks/useTheme";
import { CHAIN } from "../../lib/format";
import { search as searchApi } from "../../lib/api";

const NAV = [
  { to: "/", label: "Dashboard" },
  { to: "/blocks", label: "Blocks" },
  { to: "/txs", label: "Transactions" },
  { to: "/tokens", label: "Tokens" },
  { to: "/watchlist", label: "Watchlist" },
  { to: "/contracts", label: "Contracts" },
  { to: "/validators", label: "Validators" },
  { to: "/governance", label: "Governance" },
  { to: "/network", label: "Network" },
];

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button className="icon-btn" onClick={toggle} title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} data-testid="theme-toggle">
      <i className={`fa-solid ${theme === "dark" ? "fa-sun" : "fa-moon"}`} />
    </button>
  );
}

function AccentPicker() {
  const { accent, setAccent } = useTheme();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button className="icon-btn" onClick={() => setOpen((o) => !o)} title="Accent colour" data-testid="accent-picker">
        <i className="fa-solid fa-palette" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 panel p-2.5 flex gap-2 z-50" data-testid="accent-menu" style={{ minWidth: 0 }}>
          {ACCENTS.map((a) => (
            <button
              key={a.id}
              onClick={() => { setAccent(a.id); setOpen(false); }}
              title={a.label}
              data-testid={`accent-${a.id}`}
              className="rounded-full transition-transform hover:scale-110"
              style={{ width: 22, height: 22, background: a.c, outline: accent === a.id ? "2px solid var(--text)" : "2px solid transparent", outlineOffset: 2, cursor: "pointer" }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children, chainInfo }) {  const location = useLocation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  const doSearch = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    setErr("");
    try {
      const res = await searchApi(q.trim());
      if (res.type === "tx") navigate(`/tx/${res.value}`);
      else if (res.type === "block") navigate(`/block/${res.value}`);
      else if (res.type === "address") navigate(`/address/${res.value}`);
      setQ("");
    } catch {
      setErr("No matching block, transaction, or address found.");
      setTimeout(() => setErr(""), 3500);
    } finally {
      setBusy(false);
    }
  };

  const online = !!chainInfo;
  const gas = chainInfo ? `${chainInfo.gasPriceGwei || 0} Gwei` : "—";
  const tps = chainInfo?.blockTime ? "live" : "—";

  return (
    <div className="App min-h-screen">
      <header
        className="sticky top-0 z-40"
        style={{ background: "rgba(9,12,16,0.85)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--border)" }}
      >
        <div className="max-w-[1560px] mx-auto px-5 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
            <img src="/blokista-logo.webp" alt="Blokista" className="h-8 w-auto" style={{ maxWidth: 160 }} />
          </Link>

          <nav className="hidden xl:flex items-center gap-0.5 ml-3 overflow-x-auto">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className={`nav-link ${isActive(n.to) ? "active" : ""}`} data-testid={`nav-${n.label.toLowerCase()}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex-1" />

          <div className="hidden md:flex items-center gap-3 text-[11.5px] font-mono mr-1" style={{ color: "var(--muted)" }}>
            <span className="flex items-center gap-1.5">
              <span className={`dot ${online ? "dot-green live-dot" : "dot-red"}`} />
              {online ? "Online" : "Offline"}
            </span>
            <span style={{ color: "var(--border-2)" }}>|</span>
            <span>Gas: <span style={{ color: "var(--amber)" }}>{gas}</span></span>
            <span style={{ color: "var(--border-2)" }}>|</span>
            <span>Chain: <span style={{ color: "var(--text)" }}>{CHAIN.chainId}</span></span>
          </div>

          <div className="flex items-center gap-2">
            <Link to="/network" className="icon-btn" title="Network" data-testid="topbar-network"><i className="fa-solid fa-diagram-project" /></Link>
            <Link to="/governance" className="icon-btn" title="Governance" data-testid="topbar-governance"><i className="fa-solid fa-bell" /><span className="badge-dot" /></Link>
            <WalletButton />
            <AccentPicker />
            <ThemeToggle />
            <Link to="/watchlist" className="icon-btn" title="Watchlist" data-testid="topbar-watchlist"><i className="fa-solid fa-star" /></Link>
            <Link to="/admin" className="icon-btn" title="Admin Console" data-testid="topbar-admin"><i className="fa-solid fa-user-shield" /></Link>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="xl:hidden overflow-x-auto" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="flex gap-1 px-4 py-2">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className={`nav-link ${isActive(n.to) ? "active" : ""}`}>{n.label}</Link>
            ))}
          </div>
        </div>

        {/* Command search */}
        <div style={{ borderTop: "1px solid var(--border)" }}>
          <div className="max-w-[1560px] mx-auto px-5 py-3">
            <form onSubmit={doSearch}>
              <div className="text-center mb-2">
                <span className="eyebrow" style={{ color: "var(--amber)" }}><i className="fa-solid fa-terminal mr-1.5" />Blokista Explorer CMD</span>
              </div>
              <div className="relative max-w-3xl mx-auto">
                <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "var(--muted-2)" }} />
                <input
                  data-testid="global-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search block #, tx 0x…, address 0x… or paste a token contract"
                  className="cmd-search"
                />
                <button type="submit" data-testid="global-search-submit" disabled={busy}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 btn-amber py-2 px-4 text-[12px]">
                  {busy ? "…" : "Search"}
                </button>
              </div>
              {err && <div className="text-center mt-2 text-[12px]" style={{ color: "var(--red)" }} data-testid="search-error">{err}</div>}
            </form>
          </div>
        </div>
      </header>

      <main className="max-w-[1560px] mx-auto px-5 py-6">{children}</main>

      <footer className="max-w-[1560px] mx-auto px-5 py-8 mt-4" style={{ borderTop: "1px solid var(--border)" }}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-[12px]" style={{ color: "var(--muted)" }}>
          <div className="flex items-center gap-2">
            <img src="/blokista-logo.webp" alt="Blokista" className="h-5 w-auto opacity-90" style={{ maxWidth: 110 }} />
            <span>· Hyperledger Besu · QBFT Consensus</span>
          </div>
          <div className="font-mono">Chain ID {CHAIN.chainId} · Symbol {CHAIN.symbol} · Cancun BCC VM</div>
        </div>
      </footer>
    </div>
  );
}
